import { computed, effect, signal } from "@preact/signals";

const INTERVAL_MS = 200;

// This type declaration is required for TS to support us
// listening to the custom event.
declare global {
  interface WindowEventMap {
    "player-interface": CustomEvent<PlayerInterface>;
  }
}

type Subscriber = (currentTime: number) => void;
type UnsubscribeFn = () => void;

/**
 * This represents the programmatic interface required to
 * interact with an arbitrary media player. This normalizes
 * the API to be vendor-agnostic.
 */
export type PlayerInterface = {
  getCurrentTime: () => number;
  seekTo: (time: number) => void;
  onSeek: (callback: () => void) => UnsubscribeFn;
  onPlay: (callback: () => void) => UnsubscribeFn;
  onPause: (callback: () => void) => UnsubscribeFn;
  onPlaybackRateChange: (callback: (rate: number) => void) => UnsubscribeFn;
  onBuffer: (callback: () => void) => UnsubscribeFn;
};

const noopPlayer: PlayerInterface = {
  getCurrentTime: () => -1,
  seekTo: () => {},
  onSeek: () => () => {},
  onPlay: () => () => {},
  onPause: () => () => {},
  onPlaybackRateChange: () => () => {},
  onBuffer: () => () => {},
};

/** This Signal contains a Map of all subscribers to the player timer ticks. */
export const timerSubscribers = signal(new Map<symbol, Subscriber>());
/** This Signal contains the currently-active player interface. */
export const playerInterface = signal<PlayerInterface>(noopPlayer);

/** We compute a plain array of Subscribers from the timerSubscribers Map. */
const subsArray = computed(() => Array.from(timerSubscribers.value.values()));

/**
 * Calling `tick` will schedule a synchronization point where all of
 * the timer subscribers receive the current time.
 */
function tick() {
  // Grab this value once so that we don't run into race conditions
  // where the array length changes during the loop below.
  const subs = subsArray.value;

  // We use an old-fashioned loop to avoid the downlevel iteration
  // junk that tsc might compile this to.
  for (let i = 0; i < subs.length; i++) {
    scheduleTask(() => {
      subs[i](playerInterface.value.getCurrentTime());
    });
  }
}

function scheduleTask(callback: () => void) {
  if ("requestIdleCallback" in window) {
    // This will attempt to wait for idle time on the main thread before
    // executing the sync. The timeout is set to the same length as our
    // tick interval to ensure that this runs before the next tick.
    requestIdleCallback(callback, { timeout: INTERVAL_MS });
  } else {
    setTimeout(callback, 0);
  }
}

// This effect sets up the two conditions when a `tick` is fired:
// 1. On a regular interval
// 2. Immediately when the player seeks
effect(() => {
  const timer = setInterval(() => {
    setTimeout(tick, 0);
  }, INTERVAL_MS);
  const removeListener = playerInterface.value.onSeek(tick);

  return () => {
    clearInterval(timer);
    removeListener();
  };
});

// This effect listens for our custom event which contains a new
// player interface that we should set as the active player interface.
// It is up to each vendor to dispatch this event on the `window` where
// this app is running (e.g. `window.parent` for iframe'd players).
effect(() => {
  const eventHandler = (event: CustomEvent<PlayerInterface>) => {
    playerInterface.value = event.detail;
  };
  window.addEventListener("player-interface", eventHandler);

  return () => {
    window.removeEventListener("player-interface", eventHandler);
  };
});

/**
 * Use this function to subscribe to timer updates from the player interface.
 * The `subscribe` callback might be called at any time and is given the current
 * time of the player. Take care to avoid using the main thread too much.
 */
export function subscribeToTimer(subscriber: Subscriber): UnsubscribeFn {
  const key = Symbol();

  // NOTE: You must create a new Map instance to trigger a signal update.
  const subsCopy = new Map(timerSubscribers.value);
  subsCopy.set(key, subscriber);
  timerSubscribers.value = subsCopy;

  return () => {
    const subsCopy = new Map(timerSubscribers.value);
    subsCopy.delete(key);
    timerSubscribers.value = subsCopy;
  };
}
